import { parseFrontmatter } from './frontmatterParser';
import type { BlogPost } from './blogTypes';
import { validateBlogPost, sanitizeBlogPost, calculateReadTime } from './blogValidation';

// Error types for better error handling
export class BlogLoadError extends Error {
  constructor(
    message: string,
    public filePath: string,
    public originalError?: Error
  ) {
    super(message);
    this.name = 'BlogLoadError';
  }
}

// Dynamically import all MDX and MD files - using relative paths from utils folder
const mdxModules = import.meta.glob('../content/blog/**/*.mdx');
const mdFiles = import.meta.glob('../content/blog/**/*.md', { as: 'raw', eager: false });
let cache: Map<string, BlogPost> = new Map();
let lastCacheUpdate: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Load and parse all blog posts with comprehensive error handling
 */
export async function getAllBlogPosts(includeErrors = false): Promise<{
  posts: BlogPost[];
  errors: BlogLoadError[];
}> {
  const errors: BlogLoadError[] = [];
  const posts: BlogPost[] = [];

  // Check cache validity
  const now = Date.now();
  if (cache.size > 0 && now - lastCacheUpdate < CACHE_TTL) {
    return { posts: Array.from(cache.values()), errors: [] };
  }

  // Clear cache for fresh load
  cache.clear();

  if (import.meta.env.DEV) {
    console.log('Loading blog posts...');
    console.log('MD files found:', Object.keys(mdFiles));
    console.log('MDX files found:', Object.keys(mdxModules));
  }

  // Process MDX files
  await Promise.all(
    Object.entries(mdxModules).map(async ([filePath, loader]) => {
      try {
        // Load the MDX module
        const mdxModule = await loader() as {
          default: React.ComponentType<any>,
          frontmatter: Record<string, any>
        };
        const { default: Component, frontmatter } = mdxModule;

        // Extract slug from filename
        const filename = filePath.split('/').pop() || '';
        const slug = frontmatter.slug || filename.replace(/\.(mdx|md)$/, '');

        // Build blog post object
        const rawPost = {
          title: frontmatter.title || 'Untitled',
          excerpt: frontmatter.excerpt || '',
          category: frontmatter.category || 'Uncategorized',
          tags: Array.isArray(frontmatter.tags) ? frontmatter.tags : [],
          slug,
          content: frontmatter.body || '',
          cta: frontmatter.cta,
          compliance: frontmatter.compliance,
          publishDate: frontmatter.date || frontmatter.publishDate,
          author: frontmatter.author,
          featuredImage: frontmatter.featuredImage,
          Component,
        };

        // Sanitize the post data
        const sanitizedPost = sanitizeBlogPost(rawPost) as BlogPost;

        // Calculate read time
        sanitizedPost.readTime = calculateReadTime(sanitizedPost.content || '');

        // Validate the post
        const validation = validateBlogPost(sanitizedPost);

        // Skip posts with errors unless includeErrors is true
        if (!validation.isValid && !includeErrors) {
          const errorMessages = validation.errors.map(e => e.message).join(', ');
          errors.push(new BlogLoadError(
            `Validation failed: ${errorMessages}`,
            filePath
          ));
          return;
        }

        // Skip draft posts in production
        if (frontmatter.draft && process.env.NODE_ENV === 'production') {
          return;
        }

        // Add to results
        cache.set(slug, sanitizedPost);
        posts.push(sanitizedPost);

      } catch (error) {
        if (import.meta.env.DEV) {
          if (import.meta.env.DEV) {

            if (import.meta.env.DEV) {
              console.error('Error in blog loader:', filePath, error);
            }

          }
        }
        errors.push(new BlogLoadError(
          `Failed to load blog post: ${error instanceof Error ? error.message : 'Unknown error'}`,
          filePath,
          error instanceof Error ? error : undefined
        ));
      }
    })
  );

  // Process plain Markdown files
  if (import.meta.env.DEV) {
    // console.log('About to process MD files:', Object.entries(mdFiles).length);
  }
  await Promise.all(
    Object.entries(mdFiles).map(async ([filePath, loader]) => {
      try {
        if (import.meta.env.DEV) {
          // console.log('Loading file:', filePath);
        }
        // Load the raw markdown content
        const rawContent = await loader();
        if (import.meta.env.DEV) {
          // console.log('Loaded content length:', rawContent?.length);
        }

        // Ensure content is a string
        const contentString = typeof rawContent === 'string' ? rawContent : String(rawContent);

        // Parse frontmatter using browser-compatible parser
        const { data: frontmatter, content } = parseFrontmatter(contentString);

        // Extract slug from filename
        const filename = filePath.split('/').pop() || '';
        const slug = frontmatter.slug || filename.replace(/\.(mdx|md)$/, '');

        if (import.meta.env.DEV) {
          // console.log('Processing MD file:', filePath, 'Slug:', slug);
        }

        // Build blog post object
        const rawPost = {
          title: frontmatter.title || 'Untitled',
          excerpt: frontmatter.excerpt || '',
          category: frontmatter.category || 'Uncategorized',
          tags: Array.isArray(frontmatter.tags) ? frontmatter.tags : [],
          slug,
          content: content || frontmatter.body || '',
          cta: frontmatter.cta,
          compliance: frontmatter.compliance,
          publishDate: frontmatter.date || frontmatter.publishDate,
          author: frontmatter.author,
          featuredImage: frontmatter.featuredImage,
          Component: undefined as any, // Plain markdown doesn't have a component
        };

        // Sanitize the post data
        const sanitizedPost = sanitizeBlogPost(rawPost) as BlogPost;

        // Calculate read time
        sanitizedPost.readTime = calculateReadTime(sanitizedPost.content || '');

        // Validate the post
        const validation = validateBlogPost(sanitizedPost);

        // Skip posts with errors unless includeErrors is true
        if (!validation.isValid && !includeErrors) {
          const errorMessages = validation.errors.map(e => e.message).join(', ');
          errors.push(new BlogLoadError(
            `Validation failed: ${errorMessages}`,
            filePath
          ));
          return;
        }

        // Skip draft posts in production
        if (frontmatter.draft && process.env.NODE_ENV === 'production') {
          return;
        }

        // Add to results
        cache.set(slug, sanitizedPost);
        posts.push(sanitizedPost);

      } catch (error) {
        if (import.meta.env.DEV) {
          if (import.meta.env.DEV) {

            if (import.meta.env.DEV) {
              console.error('Error in blog loader:', filePath, error);
            }

          }
        }
        errors.push(new BlogLoadError(
          `Failed to load blog post: ${error instanceof Error ? error.message : 'Unknown error'}`,
          filePath,
          error instanceof Error ? error : undefined
        ));
      }
    })
  );

  // Update cache timestamp
  lastCacheUpdate = now;

  // Sort posts by date (newest first)
  posts.sort((a, b) => {
    const dateA = new Date(a.publishDate || 0).getTime();
    const dateB = new Date(b.publishDate || 0).getTime();
    return dateB - dateA;
  });

  return { posts, errors };
}

/**
 * Get a single post by slug with error handling
 */
export async function getBlogPostBySlug(slug: string): Promise<{
  post: BlogPost | null;
  error?: BlogLoadError;
}> {
  try {
    // Check cache first
    if (cache.has(slug)) {
      return { post: cache.get(slug)! };
    }

    // Load all posts if cache is empty
    const { posts, errors } = await getAllBlogPosts();

    if (import.meta.env.DEV) {
      // console.log('Looking for slug:', slug);
      // console.log('Available slugs:', posts.map(p => p.slug));
    }

    // Find the specific post
    const post = posts.find(p => p.slug === slug);

    if (!post) {
      return {
        post: null,
        error: new BlogLoadError(
          `Blog post with slug "${slug}" not found`,
          `slug: ${slug}`
        )
      };
    }

    return { post };
  } catch (error) {
    return {
      post: null,
      error: new BlogLoadError(
        `Failed to load blog post: ${error instanceof Error ? error.message : 'Unknown error'}`,
        `slug: ${slug}`,
        error instanceof Error ? error : undefined
      )
    };
  }
}

/**
 * Get posts by category
 */
export async function getBlogPostsByCategory(category: string): Promise<{
  posts: BlogPost[];
  errors: BlogLoadError[];
}> {
  const { posts, errors } = await getAllBlogPosts();
  const filteredPosts = posts.filter(p => p.category === category);
  return { posts: filteredPosts, errors };
}

/**
 * Get posts by tag
 */
export async function getBlogPostsByTag(tag: string): Promise<{
  posts: BlogPost[];
  errors: BlogLoadError[];
}> {
  const { posts, errors } = await getAllBlogPosts();
  const filteredPosts = posts.filter(p =>
    p.tags.some(t => t.toLowerCase() === tag.toLowerCase())
  );
  return { posts: filteredPosts, errors };
}

/**
 * Get related posts based on tags and category
 */
export async function getRelatedPosts(
  currentPost: BlogPost,
  limit = 3
): Promise<BlogPost[]> {
  const { posts } = await getAllBlogPosts();

  // Score posts by relevance
  const scoredPosts = posts
    .filter(p => p.slug !== currentPost.slug)
    .map(post => {
      let score = 0;

      // Same category = 3 points
      if (post.category === currentPost.category) score += 3;

      // Each matching tag = 2 points
      const matchingTags = post.tags.filter(tag =>
        currentPost.tags.includes(tag)
      );
      score += matchingTags.length * 2;

      return { post, score };
    })
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score);

  return scoredPosts.slice(0, limit).map(item => item.post);
}

/**
 * Search posts by query
 */
export async function searchBlogPosts(query: string): Promise<BlogPost[]> {
  const { posts } = await getAllBlogPosts();
  const searchTerm = query.toLowerCase();

  return posts.filter(post => {
    const searchableContent = [
      post.title,
      post.excerpt,
      post.content,
      post.category,
      ...post.tags
    ].join(' ').toLowerCase();

    return searchableContent.includes(searchTerm);
  });
}

/**
 * Clear the cache (useful for development)
 */
export function clearBlogCache(): void {
  cache.clear();
  lastCacheUpdate = 0;
}